<template>
  <section
    :class="[
      `asc-plot-data-tab-wrapper--container${
        secondary ? '__secondary' : '__primary'
      }`,
    ]"
    data-automation-id="wrapper_plotting_data_tab"
  >
    <template v-if="getMaxTabs > 1">
      <div class="asc-plot-data-tab-wrapper--list">
        <asc-plot-data-tab-list
          v-model="activeTab"
          :page-plot="pagePlot"
          :max-tabs="getMaxTabs"
          :plotting-data-tabs="getPlottingDataTabs"
          :readonly="readonly"
          :saving="saving"
          :disabled="disabled"
          :editable="editable"
          :display-error-message="displayErrorMessage"
          :is-tab-menu-disabled="isTabMenuDisabled"
          @addTab="handleAddTab"
          @deleteTab="handleDeleteTab"
          @updateTabView="handleUpdateTabView"
        ></asc-plot-data-tab-list>
      </div>
    </template>
    <section class="asc-plot-data-tab-wrapper--content">
      <asc-tab-body
        :value="activeTab"
        :primary="primary"
        :secondary="secondary"
        :max-tabs="getMaxTabs"
      >
        <template v-if="isTabMessageDisplayed">
          <div class="asc-plot-data-tab-wrapper--tab-message">
            <asc-label
              value="Changes to data selections apply to this tab only"
              color="#848484"
            ></asc-label>
          </div>
        </template>
        <asc-tab-item v-for="(tab, index) in getPlottingDataTabs" :key="index">
          <asc-filters
            :id="tab.id"
            button-text="Apply"
            no-placeholder
            :display-error-message="displayErrorMessage"
            :fields="tab.selection"
            :lookups="getFilterLookups"
            :readonly="isReadOnly(index)"
            :on-submit="handleApplyPagePlot"
            :on-disable-submit="handleDisableSubmit"
            :submit-button-visible="false"
            :tooltip="getGlobalFilterTooltip"
            :on-filters-change="handleFiltersChange"
          ></asc-filters>
        </asc-tab-item>
      </asc-tab-body>
    </section>
  </section>
</template>

<script>
import AscLabel from '@/components/atoms/AscLabel'
import AscTabBody from '@/components/atoms/AscTabBody'
import AscTabItem from '@/components/atoms/AscTabItem'
import AscFilters from '@/components/molecules/AscFilters'
import AscPlotDataTabList from '@/components/molecules/AscPlotDataTabList'
import { getMaxDataTabsGivenPlotTypeId } from '@/utilities/plotting'
import {
  FULL_FLIGHT_TIER_2,
  COLLINS_PHM_TIER_3,
  COLLINS_PHM_TIER_4,
  ANALYTIC_TRENDS_RISK_SCORE_PLOT_TYPE_ID,
  FLIGHT_PARAMETER_COMPARISON_PLOT_TYPE_ID,
  FLIGHT_PARAMETER_TRENDS_PLOT_TYPE_ID,
  ANALYTIC_COMPARISON_RISK_SCORE_PLOT_TYPE_ID,
} from '@/utilities/appConsts'

export default {
  name: 'AscPlotDataTabWrapper',

  components: {
    AscPlotDataTabList,
    AscTabBody,
    AscTabItem,
    AscFilters,
    AscLabel,
  },

  props: {
    pagePlot: {
      type: Object,
      default: () => {},
    },
    filterLookups: {
      type: Array,
      default: () => [],
    },
    plottingDataTabs: {
      type: Array,
      default: () => [],
    },
    primary: {
      type: Boolean,
      default: true,
    },
    secondary: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Number,
      default: null,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    isTabMenuDisabled: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    displayErrorMessage: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      activeTab:
        this.plottingDataTabs && this.plottingDataTabs.length ? 0 : null,
    }
  },

  computed: {
    getPlottingDataTabs() {
      return this.plottingDataTabs
    },

    getMaxTabs() {
      const tabs = getMaxDataTabsGivenPlotTypeId(this.pagePlot.plotTypeId)
      return tabs
    },

    getGlobalFilterTooltip() {
      return this.disabled ? 'Adjust Global Filter settings to edit.' : ''
    },

    isTabMessageDisplayed() {
      return (
        !this.isReadOnly && !this.disabled && this.plottingDataTabs.length > 1
      )
    },
    getFilterLookups() {
      if (this.pagePlot.prepopulated) {
        return this.filterLookups.map(item => {
          let updatedItems = item.items
          if (item.id === 'amcFeature') {
            updatedItems = item.items.filter(
              lookup => lookup.isRiskScore === this.pagePlot.isRiskScorePlot,
            )
          }
          return {
            ...item,
            items: updatedItems,
          }
        })
      }

      //ACMF Trends - Tiers 0-4
      //Flight Comparison & Flight Trends - Tiers 2, 3 & 4
      //Analytic Comparison & Analytic Trends - Tiers 3 & 4
      if (
        this.pagePlot.plotTypeId ===
          ANALYTIC_COMPARISON_RISK_SCORE_PLOT_TYPE_ID ||
        this.pagePlot.plotTypeId === ANALYTIC_TRENDS_RISK_SCORE_PLOT_TYPE_ID
      ) {
        return this.filterLookups.map(item => {
          let filteredTails = item.items
          if (item.id === 'tail') {
            filteredTails = item.items.filter(
              x =>
                x.airlineTierTypeId == COLLINS_PHM_TIER_3 ||
                x.airlineTierTypeId == COLLINS_PHM_TIER_4,
            )
          }
          return {
            ...item,
            items: filteredTails,
          }
        })
      } else if (
        this.pagePlot.plotTypeId == FLIGHT_PARAMETER_COMPARISON_PLOT_TYPE_ID ||
        this.pagePlot.plotTypeId == FLIGHT_PARAMETER_TRENDS_PLOT_TYPE_ID
      ) {
        let filterLookups = this.filterLookups.map(item => {
          let filteredTails = item.items
          if (item.id === 'tail') {
            filteredTails = item.items.filter(
              x =>
                x.airlineTierTypeId === FULL_FLIGHT_TIER_2 ||
                x.airlineTierTypeId === COLLINS_PHM_TIER_3 ||
                x.airlineTierTypeId === COLLINS_PHM_TIER_4,
            )
          }
          return {
            ...item,
            items: filteredTails,
          }
        })
        if (
          this.pagePlot.plotTypeId == FLIGHT_PARAMETER_COMPARISON_PLOT_TYPE_ID
        ) {
          filterLookups = filterLookups.map(item => {
            let filteredParameters = item.items
            if (item.id === 'parameter') {
              filteredParameters = item.items.filter(pa => !pa.hasStringKeys)
            }
            return {
              ...item,
              items: filteredParameters,
            }
          })
        }
        return filterLookups
      }
      return this.filterLookups
    },
  },

  watch: {
    plottingDataTabs(newDataTabs) {
      this.plottingDataTabs = newDataTabs
    },
    pagePlot: {
      deep: true,
      handler(newPagePlot) {
        this.pagePlot = newPagePlot
      },
    },
  },

  methods: {
    isReadOnly(tabIndex) {
      if (this.readonly || this.disabled || !this.editable) {
        return true
      }

      return this.plottingDataTabs[tabIndex].tabDisabled
    },

    handleApplyPagePlot(id) {
      this.$emit('applyToPlot', id)
    },

    handleDisableSubmit(id) {
      this.$emit('disableSubmit', id)
    },

    handleFiltersChange(id) {
      this.$emit('filtersChange', id)
    },

    handleUpdateTabView(tab) {
      this.$emit('updateTabView', tab.tabIndex, tab.selection === 'Hide')
    },

    handleDeleteTab(tabIndex) {
      this.$emit('deleteTab', tabIndex)

      if (this.activeTab === 0 || tabIndex === this.activeTab) {
        this.activeTab = 0
      } else {
        this.activeTab = this.activeTab - 1
      }
    },

    handleAddTab() {
      this.$emit('addTab', this.activeTab)
      this.activeTab = this.plottingDataTabs.length - 1
    },
  },
}
</script>

<style lang="scss">
.asc-plot-data-tab-wrapper--container {
  &__secondary {
    & > .asc-plot-data-tab-wrapper--list {
      background-color: $gray-0;
    }
  }
}

.asc-plot-data-tab-wrapper--content {
  .asc-tab__content--primary,
  .asc-tab__content--secondary {
    padding-top: 8px;
  }

  .asc-plot-data-tab-wrapper--tab-message {
    padding-bottom: 8px;
  }
}
</style>
